import { type Registry as Services, service } from '@ember/service';
import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';

interface PropertyRedirect {
  platform: string;
  urls: string[];
}

// Property detail model (for summary model see ShortTermRentalProperty)
export default class ShortTermRentalPropertyDetail extends Model {
  [Type] = 'short-term-rental-property-detail' as const;

  @attr('number') declare additionalFees: number;
  @attr() declare amenities: string[];
  @attr('number') declare averageRate: number;
  @attr('number') declare bathrooms: number;
  @attr('number') declare beds: number;
  @attr('number') declare capacity: number;
  @attr('string') declare currency: string;
  @attr('string') declare name: string;
  @attr() declare redirects: PropertyRedirect[];
  @attr('number') declare reviewScore: number;
  @attr('string') declare subtype: string;
  @attr('string') declare type: string;
  @attr('number') declare yearlyEstOccupancy: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedAdditionalFees() {
    if (!this.additionalFees) {
      return this.intl.t('general.missing');
    }
    return this.hotelAndMarketService.formatRate(
      this.additionalFees,
      true,
      false,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedAmenities() {
    return this.amenities.map((item) =>
      this.intl.t(`short_term_rental.amenities.${item}`),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedAverageRate() {
    if (!this.averageRate) {
      return this.intl.t('general.missing');
    }
    return this.hotelAndMarketService.formatRate(this.averageRate, true, false);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedOccupancy() {
    if (this.yearlyEstOccupancy !== 0 && !this.yearlyEstOccupancy) {
      return this.intl.t('general.missing');
    }

    return this.intl.formatNumber(this.yearlyEstOccupancy, {
      style: 'percent',
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get propertyType() {
    return this.intl.t(`short_term_rental.property_type.${this.subtype}`);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get roomCategory() {
    return this.intl.t(`short_term_rental.place_type.${this.type}`);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedRedirects() {
    return this.redirects
      .map((redirect) => {
        // Backwards compatibility for old homeaway redirects
        // Can be removed once the platform property is renamed in the API to vrbo
        if (redirect.platform === 'homeaway') {
          return {
            ...redirect,
            platform: 'vrbo',
          };
        }

        return redirect;
      })
      .sort((a, b) => a.platform.localeCompare(b.platform));
  }
}
